/**
 * Risk Control Service – QMS Phase 2
 * CRUD for risk_controls: link risk → control (type, reference, effectiveness, status).
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';

const COLLECTION = 'risk_controls';

export class RiskControlService {
  constructor() {
    this.collectionRef = collection(db, COLLECTION);
  }

  /**
   * Create a risk control
   * @param {Object} data - riskId, controlType, referenceType (protocol|standard|text), referenceId, referenceLabel, effectivenessTarget, effectivenessCurrent, controlStatus, organizationId
   */
  async createControl(data, userId) {
    const now = Timestamp.now();
    const payload = {
      riskId: data.riskId,
      riskSource: data.riskSource || 'protocol_risks',
      controlType: data.controlType,
      referenceType: data.referenceType || 'text', // 'protocol' | 'standard' | 'text'
      referenceId: data.referenceId || null,
      referenceLabel: data.referenceLabel || '',
      effectivenessTarget: data.effectivenessTarget ?? null,
      effectivenessCurrent: data.effectivenessCurrent ?? null,
      controlStatus: data.controlStatus || 'Active',
      organizationId: data.organizationId || null,
      createdAt: now,
      createdBy: userId,
      updatedAt: now,
      updatedBy: userId
    };
    const ref = await addDoc(this.collectionRef, payload);
    return ref.id;
  }

  /**
   * Get control by ID
   */
  async getControl(controlId) {
    const ref = doc(this.collectionRef, controlId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  }

  /**
   * Get all controls for a risk
   */
  async getControlsByRiskId(riskId) {
    if (!riskId) return [];
    const q = query(
      this.collectionRef,
      where('riskId', '==', riskId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  /**
   * Update control
   */
  async updateControl(controlId, updates, userId) {
    const ref = doc(this.collectionRef, controlId);
    const now = Timestamp.now();
    await updateDoc(ref, {
      ...updates,
      updatedAt: now,
      updatedBy: userId
    });
  }

  /**
   * Delete control
   */
  async deleteControl(controlId) {
    const ref = doc(this.collectionRef, controlId);
    await deleteDoc(ref);
  }
}

export const riskControlService = new RiskControlService();
